/**
 * MarketingHome · v2.6.9 batch BQ (Carrgo-inspired)
 * ==================================================
 * Public homepage. Six sections, all CMS-driven via /site/config:
 *   1. Hero — big yellow CTA + booking form on the right
 *   2. Why Choose Buller Taxi — 3 feature cards
 *   3. Service Area — Weatherford city-limits map + city logo + destinations
 *   4. Pricing — pulls /rider-prices/config; Book Now CTAs
 *   5. Quick FAQ / Call-out banner (yellow strip)
 *   6. (footer is in MarketingLayout)
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import MarketingBookForm from "@/components/marketing/MarketingBookForm";
import SeoHead from "@/components/SeoHead";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Phone, DollarSign, ShieldCheck, Car, ArrowRight, MapPin, Plane, Loader2, Clock, Sparkles, Luggage, UserCheck, Gem, Crown, Gift, Wifi, Baby, Dog, Snowflake, Star, Briefcase, Users, Smartphone } from "lucide-react";
import api from "@/lib/api";
import useSiteConfig from "@/hooks/useSiteConfig";
import usePortalHours from "@/hooks/usePortalHours";
import { QRCodeSVG } from "qrcode.react";
import HomeCtaBanner from "@/components/marketing/HomeCtaBanner";
import PublicHoursBanner from "@/components/marketing/PublicHoursBanner";

const ICONS = { DollarSign, ShieldCheck, Car, MapPin, Plane, Clock };

export default function MarketingHome() {
  const { cfg, loading } = useSiteConfig();
  const portalHours = usePortalHours();          // v2.8+E307 · live open/closed
  const isOpen = portalHours?.is_open === true;   // gate the hero "Book a Ride" CTA
  const [rates, setRates] = useState(null);
  // v2.6.9 batch BW · Auto-scan /public for city image filenames containing
  // "weatherford" / "okc" / "tulsa". Whatever the backend returns overrides
  // the CMS-stored city_grid[].image_url so dropping a new file like
  // `weatherford-skyline.jpg` into /public makes it appear instantly.
  const [autoCityAssets, setAutoCityAssets] = useState({});
  // v2.7.65+E237 · Premium Services & Add-Ons — isolated CMS section shown
  // above Taxi Rates. Fetched from its own public endpoint.
  const [premium, setPremium] = useState(null);
  // v2.7.65+E238 · Group Ride Rates section (above Premium) — own endpoint.
  const [groupRides, setGroupRides] = useState(null);
  useEffect(() => { api.get("/rider-prices/config").then((r) => setRates(r.data)).catch(() => null); }, []);
  useEffect(() => { api.get("/site/city-assets").then((r) => setAutoCityAssets(r.data?.assets || {})).catch(() => setAutoCityAssets({})); }, []);
  useEffect(() => { api.get("/site/premium-services").then((r) => setPremium(r.data)).catch(() => setPremium(null)); }, []);
  useEffect(() => { api.get("/site/group-rides").then((r) => setGroupRides(r.data)).catch(() => setGroupRides(null)); }, []);

  if (loading || !cfg) {
    return (
      <MarketingLayout>
        <div className="py-24 text-center text-zinc-500"><Loader2 className="h-7 w-7 animate-spin mx-auto"/></div>
      </MarketingLayout>
    );
  }
  const h = cfg.home;
  const g = cfg.global;
  // Batch BU · QR + phone graphic both deep-link to the SAME booking page on
  // whatever host we're on (preview → preview, prod → bullertaxi.com).
  const bookingUrl = `${typeof window !== "undefined" ? window.location.origin : "https://bullertaxi.com"}/ride/login`;

  return (
    <MarketingLayout autoAssets={autoCityAssets}>
      {/* v2.7.0 batch CT · Task 4 — Per-page SEO meta. The homepage owns
          the strongest regional keyword saturation since it captures the
          highest share of "taxi weatherford ok" / "taxi clinton ok" search
          intent. SeoHead mutates the <head> on mount, restores on unmount. */}
      <SeoHead
        title="Taxi Weatherford, Oklahoma & Taxi Clinton, OK"
        description="Buller Taxi Services, LLC — Taxi Weatherford, Oklahoma and Taxi Clinton, OK. 24/7 dispatch, airport runs, and fair flat-fare rides across Weatherford OK, Clinton OK, and all of Western Oklahoma. Call (901) 459-9448 or book online."
        canonical="/"
        hreflangs={[
          { lang: "en-US", href: "https://bullertaxi.com/" },
          { lang: "en", href: "https://bullertaxi.com/" },
          { lang: "x-default", href: "https://bullertaxi.com/" },
        ]}
      />
      {/* ── 1 · HERO ──────────────────────────────────────────────────
          v2.6.9 batch CE · CMS upload now WINS over the auto-scanner.
          Priority: h.hero_image_url (admin upload) → autoCityAssets.hero
          (/public file fallback) → empty.
          E261 · The CTA banner now lives INSIDE the hero so the hero
          background flows behind it (no black strip) and it bleeds up
          flush under the navigation menu. */}
      <section
        className="relative overflow-hidden border-b border-amber-400/70"
        style={{
          backgroundImage: `linear-gradient(rgba(0,0,0,0.78), rgba(0,0,0,0.62)), url("${h.hero_image_url || autoCityAssets.hero || ""}")`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        data-testid="hero"
      >
        {/* v2.8+E302 · Small business-hours strip — sits ABOVE the DON'T RISK
            IT CTA banner, intentionally smaller so it doesn't compete. */}
        <PublicHoursBanner variant="strip"/>
        {/* CTA banner — admin-toggleable, editable, visitor-dismissible */}
        <HomeCtaBanner/>
        <div className="max-w-7xl mx-auto px-5 pt-6 pb-14 sm:pb-20 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 items-stretch">
          <div className="flex flex-col justify-center" data-testid="hero-cta">
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-amber-400 font-bold mb-3">
              <span className="h-px w-8 bg-amber-400"/>{h.hero_eyebrow}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-zinc-100 leading-tight mb-3">{h.hero_title}</h1>
            <p className="text-lg text-zinc-300 mb-5 max-w-xl">{h.hero_subtitle}</p>
            <div className="bg-amber-400 text-zinc-950 inline-flex flex-col px-6 py-5 rounded-md shadow-lg shadow-amber-500/30 mb-3">
              <span className="text-2xl sm:text-3xl font-black tracking-tight leading-tight">{h.cta_heading}</span>
              <span className="text-sm font-semibold opacity-90">{h.cta_subtext}</span>
              {/* v2.6.9 batch BX · Thin divider directly above the phone
                  number, below the "Let our drivers ensure..." subtitle. */}
              <hr className="border-0 border-t border-zinc-950/40 my-3" data-testid="hero-phone-divider"/>
              <a href={`tel:${(h.cta_phone || "").replace(/\D/g, "")}`} className="inline-flex items-center gap-2 text-2xl sm:text-3xl font-black tracking-tight" data-testid="hero-call-phone">
                <Phone className="h-6 w-6"/>{h.cta_phone}
              </a>
            </div>
            <div className="flex flex-wrap gap-3">
              {/* v2.8+E307 · Open-now "Book a Ride" CTA — matches the green
                  "Open now" hours badge; only shows during business hours to
                  drive instant bookings. */}
              {isOpen && (
                <Link to="/ride/login" data-testid="hero-open-book-btn">
                  <Button className="bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-extrabold uppercase tracking-wider shadow-lg shadow-emerald-500/30">
                    <span className="mr-1.5 inline-block h-2 w-2 rounded-full bg-zinc-950/70 animate-pulse"/>
                    Book a Ride <ArrowRight className="h-4 w-4 ml-1"/>
                  </Button>
                </Link>
              )}
              <Link to="/ride/login" data-testid="hero-cta-book">
                <Button className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-extrabold uppercase tracking-wider">Book Online <ArrowRight className="h-4 w-4 ml-1"/></Button>
              </Link>
              <Link to="/rates" data-testid="hero-cta-rates">
                <Button variant="outline" className="bg-transparent border-amber-400 text-amber-300 hover:bg-amber-400 hover:text-zinc-950 font-extrabold uppercase tracking-wider">See Rates</Button>
              </Link>
            </div>
            {/* Batch BS · "COMING SOON — In-Motion WiFi" pill, directly beneath the Book Online button */}
            <div className="mt-4">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-400/15 border border-amber-400/60 text-amber-300 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em]" data-testid="hero-wifi-badge">
                <Wifi className="h-3.5 w-3.5"/> Coming Soon — In-Motion WiFi
              </span>
            </div>
            {/* Batch BS · Loyalty Program teaser link */}
            <Link to="/loyalty" className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-amber-300 hover:text-amber-200 group" data-testid="hero-loyalty-link">
              <Gift className="h-4 w-4"/> Ask about our Loyalty Program — 10th ride free
              <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5"/>
            </Link>
            {/* Batch BR · Accepted payment-method brand badges (uniform tiles) */}
            <PaymentBadges/>
          </div>
          <div className="flex items-center justify-center" data-testid="hero-form-col">
            {/* Batch BT/BU · New dual-phone app graphic (transparent PNG) with
                a real scannable QR + store caption below. Phones + QR both
                deep-link to the booking page. Quick Booking form preserved
                (hidden) below for future revival. */}
            <div className="w-full max-w-[600px] mx-auto">
              <Link to="/ride/login" className="block group" data-testid="hero-loyalty-graphic" title="Book online">
                <img
                  src="/buller-app-phones-transparent.png?v=2"
                  alt="Buller Taxi Rider Portal app shown on an iPhone and an Android phone — book a ride in seconds"
                  loading="eager"
                  className="w-full h-auto object-contain drop-shadow-2xl transition-transform duration-300 group-hover:scale-[1.01]"
                  data-testid="hero-loyalty-image"
                />
              </Link>
              {/* Scannable QR + "Available on iPhone & Android" caption */}
              <div className="mt-5 flex items-center justify-center gap-4">
                <a
                  href={bookingUrl}
                  className="shrink-0 rounded-xl bg-white p-2 shadow-xl ring-1 ring-black/10 hover:scale-[1.04] transition-transform"
                  data-testid="hero-qr-book"
                  aria-label="Scan to book a ride"
                >
                  <QRCodeSVG value={bookingUrl} size={78} level="M" fgColor="#0b0b0c" bgColor="#ffffff"/>
                </a>
                <div className="text-left">
                  <div className="text-sm font-black text-amber-300 uppercase tracking-wide" data-testid="hero-qr-label">Scan to book instantly</div>
                  <div className="text-xs text-zinc-400 mt-0.5">Point your phone camera at the code</div>
                  <div className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] font-bold text-zinc-300" data-testid="hero-app-caption">
                    <Smartphone className="h-3.5 w-3.5 text-amber-400"/> Available on iPhone &amp; Android
                  </div>
                </div>
              </div>
            </div>
            {/* HIDDEN — Quick Booking form kept for future revival (do not delete). */}
            {false && (
              <MarketingBookForm bgUrl={h.quick_fare_background_url || autoCityAssets.fare || ""}/>
            )}
          </div>
        </div>
      </section>

      {/* v2.7.0 batch CT · Task 4 — Hidden semantic SEO block. Visually
          hidden via `sr-only` but fully indexable by Google/Bing. Saturates
          the page with the user-specified regional keyword permutations so
          the homepage ranks for every spelling/casing of the target query. */}
      <section className="sr-only" aria-hidden="false" data-testid="seo-regional-keywords">
        <h2>Taxi Weatherford, Oklahoma · Taxi Clinton, OK · 24/7 Dispatch</h2>
        <p>
          Buller Taxi Services, LLC is the trusted Taxi Weatherford, Oklahoma and
          Taxi Clinton, OK provider for Western Oklahoma. Whether you need a
          taxi weatherford oklahoma run to Will Rogers World Airport, a fast
          Taxi Weatherford, OK ride to the SWOSU campus, or a reliable
          taxi weatherford ok pickup from a downtown restaurant — our 24/7
          dispatch keeps Weatherford Oklahoma and Clinton Oklahoma moving.
        </p>
        <h3>Service Area</h3>
        <ul>
          <li>Taxi Weatherford, Oklahoma</li>
          <li>Taxi Weatherford, OK</li>
          <li>taxi weatherford, oklahoma</li>
          <li>taxi weatherford, ok</li>
          <li>Taxi Clinton, OK</li>
          <li>Clinton, Oklahoma taxi service</li>
          <li>Weatherford, Oklahoma airport runs</li>
        </ul>
      </section>

      {/* v2.6.9 batch BW · Portal Quick-Links MOVED from between hero and
          "Why Choose" → now sits between the Taxi Rates section and the
          yellow "Need a ride right now?" strip (see below, just under the
          "View Full Pricing Details" link). This makes more sense as the
          final call-to-action: rider sees prices first, then picks their
          portal. */}

      {/* ── 2 · WHY CHOOSE ──────────────────────────────────────────── */}
      <section className="bg-zinc-950 py-16 border-b border-zinc-900" data-testid="why-choose">
        <div className="max-w-7xl mx-auto px-5">
          <div className="text-center mb-10">
            <div className="text-[10px] uppercase tracking-[0.4em] text-amber-400 font-bold">Why Riders Choose Us</div>
            <h2 className="text-3xl sm:text-4xl font-black text-zinc-100 mt-2">{h.why_choose_heading}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {h.why_choose_items.map((it, i) => {
              const Icon = ICONS[it.icon] || ShieldCheck;
              const hasBadge = !!it.image_url;
              // v2.6.9 batch CS · Use a stable composite key (title + icon)
              // instead of the array index so React doesn't reuse the wrong
              // <Card> when admins reorder items in the CMS.
              const stableKey = `${it.title || "item"}__${it.icon || "noicon"}__${i}`;
              return (
                <Card key={stableKey} className="bg-zinc-900 border-zinc-800 hover:border-amber-400/60 transition-colors p-6 group" data-testid={`why-choose-item-${i}`}>
                  {/* v2.6.9 batch CF · Uploaded badge image OVERRIDES the
                      lucide icon when present. Sized to match the icon
                      footprint (h-14 w-14) so the rhythm stays identical. */}
                  {hasBadge ? (
                    <div className="h-14 w-14 mb-4 flex items-center justify-center" data-testid={`why-choose-item-${i}-badge`}>
                      <img
                        src={it.image_url}
                        alt={it.title}
                        loading="lazy"
                        className="h-14 w-14 object-contain drop-shadow-[0_2px_8px_rgba(250,204,21,0.25)] group-hover:scale-105 transition-transform"
                      />
                    </div>
                  ) : (
                    <div className="h-14 w-14 rounded-xl bg-amber-400/15 border-2 border-amber-400 flex items-center justify-center text-amber-400 mb-4 group-hover:bg-amber-400 group-hover:text-zinc-950 transition-colors">
                      <Icon className="h-7 w-7"/>
                    </div>
                  )}
                  <h3 className="text-lg font-extrabold text-zinc-100 mb-1.5">{it.title}</h3>
                  <p className="text-sm text-zinc-400 leading-relaxed">{it.desc || it.description}</p>
                </Card>
              );
            })}
          </div>

          {/* v2.6.9 batch BU · CITY GRID — 3-column responsive gallery of
              partner cities sitting directly under the "Why Choose" tabs.
              Each card has a 1px solid border + box-shadow per spec. A
              "BOOK NOW" overlay button deep-links to the rider booking flow
              with destination + meter_hint prefilled (Weatherford → city
              meter; OKC/Tulsa → statewide meter). Submissions land in
              Admin → Book A Ride via /api/site/quick-booking. */}
          {h.city_grid_enabled !== false && Array.isArray(h.city_grid) && h.city_grid.length > 0 && (
            <div className="mt-12" data-testid="city-grid">
              <div className="text-center mb-6">
                <div className="text-[10px] uppercase tracking-[0.35em] text-amber-400/80 font-bold">Service Coverage</div>
                <h3 className="text-2xl sm:text-3xl font-black text-zinc-100 mt-1">{h.city_grid_heading || "Where We Go"}</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {h.city_grid.map((c, i) => {
                  // v2.6.9 batch CE · CMS upload (c.image_url) wins over the
                  // /public auto-scanner match. Scanner is only a fallback
                  // when admin hasn't uploaded anything.
                  const cityWithAuto = c.image_url
                    ? c
                    : (autoCityAssets?.[c.slug] ? { ...c, image_url: autoCityAssets[c.slug] } : c);
                  return <CityCard key={c.slug || i} city={cityWithAuto}/>;
                })}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ── 3 · SERVICE AREA ──────────────────────────────────────────
          v2.6.9 batch CF · 1 px yellow border on top + bottom of the
          section (was 6 px) per spec — thinner, more refined frame. */}
      <section
        className="py-16 border-y border-amber-400"
        style={
          h.service_area_use_color === false && h.service_area_bg_image_url
            ? {
                backgroundImage: `linear-gradient(rgba(0,0,0,0.65), rgba(0,0,0,0.75)), url("${h.service_area_bg_image_url}")`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundColor: h.service_area_bg_color || "#000000",
              }
            : { backgroundColor: h.service_area_bg_color || "#000000" }
        }
        data-testid="service-area"
      >
        <div className="max-w-7xl mx-auto px-5">
          <div className="text-center mb-8">
            <div className="text-[10px] uppercase tracking-[0.4em] text-amber-400 font-bold">Where We Operate</div>
            <h2 className="text-3xl sm:text-4xl font-black text-zinc-100 mt-2">{h.service_area_heading}</h2>
            <p className="text-sm text-zinc-400 mt-2">{h.service_area_subtitle}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
            {/* Coverage map column — matches the destinations column header for
                a clean, symmetric two-column layout. */}
            <div data-testid="service-area-map-col">
              <div className="flex items-center justify-between gap-3 mb-4 min-h-[48px]">
                <h3 className="text-xl font-extrabold text-amber-300 uppercase tracking-wide">Coverage Map</h3>
                {h.service_area_logo_url && (
                  <img
                    src={h.service_area_logo_url}
                    alt="City of Weatherford"
                    loading="lazy"
                    decoding="async"
                    className="h-12 w-auto flex-shrink-0"
                    data-testid="service-area-logo"
                  />
                )}
              </div>
              <ServiceAreaMap/>
              <p className="text-xs text-zinc-500 mt-3">The yellow outline marks Weatherford city limits — flat in-town fares apply.</p>
            </div>

            {/* Destinations column */}
            <div data-testid="service-area-destinations">
              <div className="flex items-center gap-3 mb-4 min-h-[48px]">
                <h3 className="text-xl font-extrabold text-amber-300 uppercase tracking-wide">Destinations We Cover</h3>
              </div>
              {/* v2.8 · Uniform grid — equal-height cards with vertically
                  centered content so single- and multi-line entries line up. */}
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-stretch">
                {h.service_area_destinations.map((d, i) => {
                  const cycles = [
                    { hoverBorder: "hover:border-amber-400",    hoverBadge: "group-hover:bg-amber-400 group-hover:text-zinc-950",    hoverText: "group-hover:text-amber-300" },
                    { hoverBorder: "hover:border-emerald-400",  hoverBadge: "group-hover:bg-emerald-400 group-hover:text-zinc-950", hoverText: "group-hover:text-emerald-300" },
                    { hoverBorder: "hover:border-sky-400",      hoverBadge: "group-hover:bg-sky-400 group-hover:text-zinc-950",     hoverText: "group-hover:text-sky-300" },
                    { hoverBorder: "hover:border-fuchsia-400",  hoverBadge: "group-hover:bg-fuchsia-400 group-hover:text-zinc-950", hoverText: "group-hover:text-fuchsia-300" },
                  ];
                  const c = cycles[i % cycles.length];
                  return (
                    <li
                      key={`dest__${d}`}
                      className={`group flex items-center gap-3 h-full min-h-[64px] bg-zinc-900 border border-zinc-800 rounded-lg px-4 py-3 ${c.hoverBorder} cursor-pointer hover:bg-zinc-900/80 hover:scale-[1.015] hover:shadow-lg`}
                      style={{ transition: "all 0.3s ease" }}
                      data-testid={`service-area-dest-${i}`}
                    >
                      <div className={`h-7 w-7 rounded-full bg-amber-400 text-zinc-950 flex items-center justify-center font-extrabold text-xs flex-shrink-0 ${c.hoverBadge}`} style={{ transition: "background-color 0.3s ease, color 0.3s ease" }}>{i + 1}</div>
                      <span className={`text-sm text-zinc-200 font-semibold leading-snug ${c.hoverText}`} style={{ transition: "color 0.3s ease" }}>{d}</span>
                    </li>
                  );
                })}
              </ul>
              <p className="text-xs text-zinc-500 mt-4">Need a long-distance fare quote? <Link to="/contact" className="text-amber-300 hover:text-amber-200 underline">Contact dispatch</Link> for an immediate quote.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── 3.4 · GROUP RIDE RATES (E238) — above Premium Services ──── */}
      {groupRides?.section?.enabled && <GroupRideRatesSection data={groupRides.section}/>}

      {/* ── 3.5 · PREMIUM SERVICES & ADD-ONS (E237) ──────────────────
          Isolated CMS section rendered directly above Taxi Rates. */}
      {premium?.enabled && <PremiumServicesSection data={premium}/>}

      {/* ── 4 · PRICING ────────────────────────────────────────────── */}
      {h.show_pricing !== false && (
        <section className="bg-zinc-950 py-16 border-b border-zinc-900" data-testid="home-pricing">
          <div className="max-w-7xl mx-auto px-5">
            <div className="text-center mb-8">
              <div className="text-[10px] uppercase tracking-[0.4em] text-amber-400 font-bold">Transparent · Honest</div>
              <h2 className="text-3xl sm:text-4xl font-black text-zinc-100 mt-2">Our Taxi Rates</h2>
              <p className="text-sm text-zinc-400 mt-2 max-w-xl mx-auto">Every fare is metered. No hidden fees. See the rate ranges below — book online to lock in a live quote.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {(rates?.panels || []).map((p) => {
                const color = p.color === "amber"
                  ? { ring: "border-amber-400", chip: "text-amber-300", btn: "bg-amber-400 hover:bg-amber-300 text-zinc-950" }
                  : { ring: "border-emerald-500", chip: "text-emerald-300", btn: "bg-emerald-400 hover:bg-emerald-300 text-zinc-950" };
                return (
                  <Card key={p.id} className={`bg-zinc-900 border-2 ${color.ring} p-6`} data-testid={`home-pricing-${p.id}`}>
                    <div className={`text-[10px] uppercase tracking-widest ${color.chip} font-bold`}>{p.label}</div>
                    <h3 className="text-2xl font-extrabold text-zinc-100 mt-1">{p.title}</h3>
                    <p className="text-xs text-zinc-500 mt-1 mb-4">{p.subtitle}</p>
                    <ul className="space-y-1.5 mb-4">
                      {p.rates.slice(0, 5).map((r, i) => (
                        <li key={`rate__${p.id}__${r.k || i}`} className="flex justify-between text-sm">
                          <span className="text-zinc-400 text-[12px] uppercase tracking-wider">{r.k}</span>
                          <span className={`meter-display font-extrabold ${r.hi ? color.chip : "text-zinc-100"}`}>{r.v}</span>
                        </li>
                      ))}
                    </ul>
                    <div className={`rounded-lg border-2 ${color.ring} px-4 py-3 mb-4 bg-black`}>
                      <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold">{p.estimate_label}</div>
                      <div className={`meter-display text-2xl font-extrabold ${color.chip} leading-none mt-1`}>{p.estimate}</div>
                    </div>
                    <Link to="/ride/login" data-testid={`home-pricing-${p.id}-book`}>
                      <Button className={`w-full ${color.btn} font-extrabold uppercase tracking-wider`}>Book Now <ArrowRight className="h-4 w-4 ml-1.5"/></Button>
                    </Link>
                  </Card>
                );
              })}
            </div>
            <div className="text-center mt-6">
              <Link to="/rates" className="text-amber-300 hover:text-amber-200 underline text-sm font-semibold uppercase tracking-wider">View Full Pricing Details →</Link>
            </div>
          </div>
        </section>
      )}

      {/* ── PORTAL QUICK-LINKS — v2.6.9 batch BW · MOVED here (was between
          hero and "Why Choose"). Now sits immediately below the
          "View Full Pricing Details" link as the final, decisive CTA. Same
          3-button layout + first-visit tooltip pulse. */}
      {h.portal_quicklinks_enabled !== false && (
        <section className="bg-zinc-950 py-12 border-b border-zinc-900" data-testid="portal-quicklinks">
          <div className="max-w-7xl mx-auto px-5">
            <div className="text-center mb-8">
              <div className="text-[10px] uppercase tracking-[0.4em] text-amber-400 font-bold">Get Started</div>
              <h2 className="text-2xl sm:text-3xl font-black text-zinc-100 mt-2">Pick Your Portal</h2>
              <p className="text-sm text-zinc-400 mt-1.5">Three taps to dispatch — choose your role.</p>
            </div>
            <FirstVisitTooltipWrapper>
              {/* v2.6.9 batch CC · Tighter gap — was gap-3 sm:gap-4 (12-16 px)
                  → now gap-0 sm:gap-1 (0-4 px). The button assets carry their
                  own transparent margin so they still read as 3 distinct
                  buttons without feeling crowded. */}
              <div className="flex flex-row items-center justify-center gap-0 sm:gap-1 flex-wrap" data-testid="portal-quicklinks-row">
                <PortalLinkBtn
                  href="/ride/login"
                  src={h.portal_quicklink_rider_url}
                  alt="Rider Login"
                  testid="portal-quicklink-rider"
                />
                <PortalLinkBtn
                  href="/login"
                  src={h.portal_quicklink_driver_url}
                  alt="Driver Login"
                  testid="portal-quicklink-driver"
                />
                <PortalLinkBtn
                  href="/admin/login"
                  src={h.portal_quicklink_dispatch_url}
                  alt="Dispatch Login"
                  testid="portal-quicklink-dispatch"
                />
              </div>
            </FirstVisitTooltipWrapper>

            {/* v2.8 · "Catch our ad on the following" Wright Radio Network
                partner banner (CMS-editable image + clickable station links). */}
            {h.portal_ad_banner_enabled !== false && (h.portal_ad_banner_url || (h.portal_ad_banner_links || []).length > 0) && (
              <div className="mt-10 max-w-4xl mx-auto" data-testid="portal-ad-banner">
                {h.portal_ad_banner_url && (
                  h.portal_ad_banner_link ? (
                    <a href={h.portal_ad_banner_link} target="_blank" rel="noopener noreferrer" className="block transition-transform hover:scale-[1.005]" data-testid="portal-ad-banner-link">
                      <img
                        src={h.portal_ad_banner_url}
                        alt="Catch our ad on the following — Wright Radio Network stations"
                        loading="lazy"
                        className="w-full h-auto rounded-xl shadow-lg shadow-black/30"
                      />
                    </a>
                  ) : (
                    <img
                      src={h.portal_ad_banner_url}
                      alt="Catch our ad on the following — Wright Radio Network stations"
                      loading="lazy"
                      className="w-full h-auto rounded-xl shadow-lg shadow-black/30"
                    />
                  )
                )}
                {Array.isArray(h.portal_ad_banner_links) && h.portal_ad_banner_links.filter((s) => s && s.label).length > 0 && (
                  <div className="mt-3 flex flex-wrap items-center justify-center gap-2" data-testid="portal-ad-banner-links">
                    {h.portal_ad_banner_links.filter((s) => s && s.label).map((s, i) => (
                      <a
                        key={`station__${i}`}
                        href={s.url || "#"}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[11px] font-bold uppercase tracking-wide text-zinc-300 bg-zinc-900 border border-zinc-800 rounded-full px-3 py-1.5 hover:text-amber-300 hover:border-amber-400"
                        style={{ transition: "color 0.2s ease, border-color 0.2s ease" }}
                        data-testid={`portal-ad-station-${i}`}
                      >
                        {s.label}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      )}

      {/* v2.6.9 batch BZ · "Need a ride right now?" CTA strip REMOVED per
          user spec. The portal quick-links section above already serves as
          the final call-to-action; the duplicate yellow/magenta/gray strip
          was redundant. */}
      {/* v2.6.9 batch BU · The standalone Bison brand-strip that previously
          lived directly under "Need a ride right now?" has been removed.
          Bison co-branding now lives at the VERY bottom of the global site,
          below the copyright line (see MarketingLayout footer). */}
    </MarketingLayout>
  );
}


/**
 * Batch BR · PaymentBadges — uniform, presentable "official-style" brand
 * tiles for the accepted card networks. Every tile is the SAME fixed size
 * (h-9 · w-14) on a white face with a subtle ring so the row reads clean and
 * balanced and never oversized.
 */
function PaymentBadges() {
  const tile = "h-9 w-14 rounded-md bg-white flex items-center justify-center shadow-sm ring-1 ring-black/10 shrink-0";
  return (
    <div className="mt-7" data-testid="hero-payment-badges">
      <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-400 font-bold mb-2.5">Accepted Payment Methods</div>
      <div className="flex flex-wrap items-center gap-2.5">
        <div className={tile} data-testid="hero-card-visa">
          <span className="text-[15px] font-black italic leading-none tracking-tight" style={{ color: "#1A1F71" }}>VISA</span>
        </div>
        <div className={tile} data-testid="hero-card-mastercard">
          <span className="relative inline-flex items-center" aria-label="Mastercard">
            <span className="h-5 w-5 rounded-full" style={{ backgroundColor: "#EB001B" }}/>
            <span className="h-5 w-5 rounded-full -ml-2.5" style={{ backgroundColor: "#F79E1B", mixBlendMode: "multiply" }}/>
          </span>
        </div>
        <div className={tile} data-testid="hero-card-amex">
          <span className="text-[9px] font-black leading-none tracking-tight text-white px-1.5 py-1 rounded-[3px]" style={{ backgroundColor: "#2E77BC" }}>AMEX</span>
        </div>
        <div className={tile} data-testid="hero-card-discover">
          <span className="text-[10px] font-black leading-none tracking-tight" style={{ color: "#1a1a1a" }}>
            DISC<span style={{ color: "#FF6000" }}>O</span>VER
          </span>
        </div>
      </div>
    </div>
  );
}


/**
 * ServiceAreaMap · uses Google Maps to render the Weatherford geofence
 * polygon with edge-opacity styling. Pulls the polygon from the public
 * /geo/city-polygon endpoint so the boundary stays in sync with dispatch.
 */
function ServiceAreaMap() {
  const [poly, setPoly] = useState([]);
  const [mapReady, setMapReady] = useState(false);
  const [mapFailed, setMapFailed] = useState(false);
  useEffect(() => { api.get("/geo/city-polygon").then((r) => setPoly(r.data.polygon || [])).catch(() => null); }, []);
  const [mapEl, setMapEl] = useState(null);

  useEffect(() => {
    if (!mapEl || !poly.length) return;
    // v2.6.9 batch BQ-2 · Reuse the global Google Maps loader instead of
    // injecting a parallel <script> tag. Multiple loads conflict and break
    // `google.maps.Map`. We poll a couple seconds for the script — whichever
    // sibling component (AddressAutocomplete, LiveDispatchMap) lands first.
    let cancelled = false;
    const start = Date.now();
    const waitForMaps = () => new Promise((resolve) => {
      const tick = () => {
        if (cancelled) return;
        if (window.google?.maps?.Map) return resolve();
        if (Date.now() - start > 12000) return resolve();  // give up after 12s
        setTimeout(tick, 200);
      };
      tick();
    });
    waitForMaps().then(() => {
      if (cancelled) return;
      if (!window.google?.maps?.Map) { setMapFailed(true); return; }
      const maps = window.google.maps;
      // v2.6.9 batch BS-2 · Center on the polygon's actual centroid
      // (computed from the OSM-derived city-limits ring). Light/white
      // minimalist Google Maps theme so the geofence and the city pop.
      const center = { lat: 35.5396, lng: -98.6876 };
      const map = new maps.Map(mapEl, {
        center,
        zoom: 12,
        disableDefaultUI: true,
        zoomControl: true,
        styles: [
          { elementType: "geometry", stylers: [{ color: "#f5f5f5" }] },
          { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
          { elementType: "labels.text.fill", stylers: [{ color: "#616161" }] },
          { elementType: "labels.text.stroke", stylers: [{ color: "#ffffff" }] },
          { featureType: "administrative.land_parcel", elementType: "labels", stylers: [{ visibility: "off" }] },
          { featureType: "administrative.locality", elementType: "labels.text.fill", stylers: [{ color: "#bdbdbd" }] },
          { featureType: "poi", stylers: [{ visibility: "off" }] },
          { featureType: "road", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
          { featureType: "road.arterial", elementType: "labels.text.fill", stylers: [{ color: "#757575" }] },
          { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
          { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: "#dadada" }] },
          { featureType: "road.highway", elementType: "labels.text.fill", stylers: [{ color: "#616161" }] },
          { featureType: "transit", stylers: [{ visibility: "off" }] },
          { featureType: "transit.line", elementType: "geometry", stylers: [{ color: "#e5e5e5" }] },
          { featureType: "transit.station", elementType: "geometry", stylers: [{ color: "#eeeeee" }] },
          { featureType: "water", elementType: "geometry", stylers: [{ color: "#c9e9f5" }] },
          { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#9e9e9e" }] },
        ],
      });
      new maps.Polygon({
        paths: poly.map(([lat, lng]) => ({ lat, lng })),
        // v2.6.9 batch BX · Bright high-visibility yellow perimeter at a
        // much thinner stroke (was 2.5 px black, now 1.4 px #facc15) so the
        // geofence reads as a clean accent line on the light map theme.
        strokeColor: "#facc15",
        strokeOpacity: 1.0,
        strokeWeight: 1.4,
        fillColor: "#facc15",
        fillOpacity: 0.18,
        map,
      });
      if (!cancelled) setMapReady(true);
    });
    return () => { cancelled = true; };
  }, [mapEl, poly]);

  return (
    <div className="relative rounded-2xl overflow-hidden border-2 border-amber-400 shadow-xl shadow-black/30 bg-zinc-900" style={{ height: 320 }} data-testid="service-area-map">
      <div ref={setMapEl} className="w-full h-full"/>
      {!mapReady && (
        <div
          className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 bg-gradient-to-br from-zinc-900 to-zinc-950 text-center px-6"
          data-testid="service-area-map-placeholder"
        >
          <MapPin className={`h-9 w-9 text-amber-400/80 mb-1 ${mapFailed ? "" : "animate-pulse"}`}/>
          <span className="text-sm font-black text-zinc-100 uppercase tracking-wide">Weatherford, Oklahoma</span>
          <span className="text-xs text-zinc-500">{mapFailed ? "City-limits coverage + statewide travel" : "Loading service map…"}</span>
        </div>
      )}
    </div>
  );
}

/**
 * v2.6.9 batch BW · FirstVisitTooltipWrapper — wraps the portal-quicklinks
 * grid and shows a small "First time? → Tap RIDER LOGIN" coachmark above
 * the Rider button. The tooltip uses localStorage to track if the visitor
 * has already seen it; once dismissed (or 10 seconds elapsed naturally),
 * it never shows again for that browser. The pointer bounce uses a small
 * CSS keyframe (.portal-coachmark-arrow) declared in App.css.
 */
function FirstVisitTooltipWrapper({ children }) {
  const STORAGE_KEY = "rtg.portal_coachmark_seen.v1";
  const [show, setShow] = useState(false);
  useEffect(() => {
    try {
      if (localStorage.getItem(STORAGE_KEY) === "1") return;
      // Defer 1.2s so the page settles + buttons animate in first.
      const t = setTimeout(() => setShow(true), 1200);
      return () => clearTimeout(t);
    } catch (err) {
      // localStorage blocked (private mode / Safari ITP) — coachmark stays hidden.
      // Logged at debug level so silent failures are traceable in DevTools.
      // eslint-disable-next-line no-console
      console.debug("[coachmark] localStorage read blocked:", err?.message || err);
      return undefined;
    }
  }, []);
  const dismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch (err) {
      // eslint-disable-next-line no-console
      console.debug("[coachmark] localStorage write blocked:", err?.message || err);
    }
    setShow(false);
  };
  // Auto-dismiss after 10s of being visible.
  useEffect(() => {
    if (!show) return undefined;
    const t = setTimeout(dismiss, 10_000);
    return () => clearTimeout(t);
  }, [show]);
  return (
    <div className="relative">
      {show && (
        <div
          className="absolute left-1/2 -translate-x-1/2 sm:left-[16.67%] sm:translate-x-0 -top-16 z-20"
          data-testid="portal-coachmark"
        >
          <div className="relative bg-amber-400 text-zinc-950 rounded-xl px-4 py-2.5 shadow-2xl shadow-amber-500/40 font-bold text-sm whitespace-nowrap flex items-center gap-2.5">
            <span>First time? <span className="text-zinc-900 font-black">Tap RIDER LOGIN</span></span>
            <button
              type="button"
              onClick={dismiss}
              aria-label="Dismiss tooltip"
              data-testid="portal-coachmark-dismiss"
              className="text-zinc-900/70 hover:text-zinc-900 transition-colors text-lg leading-none -mr-1"
            >×</button>
            {/* Pointer/arrow into the rider button */}
            <span className="portal-coachmark-arrow absolute left-1/2 sm:left-8 -translate-x-1/2 sm:translate-x-0 -bottom-[10px] w-0 h-0" />
          </div>
        </div>
      )}
      {children}
    </div>
  );
}

/**
 * v2.6.9 batch BU/BV · PortalLinkBtn — taxi-themed image button used in the
 * Portal Quick-Links strip. Hover effects: scale-up + brightness boost +
 * golden glow shadow + subtle counter-tilt. Click effect: snap-down (active
 * scale 0.96). At rest: a soft 3-second pulse glow so the button feels
 * "alive" without being distracting. Hardware-accelerated transforms only.
 * 30-50% width inside its grid cell so we never break safe-area.
 */
function PortalLinkBtn({ href, src, alt, testid }) {
  if (!src) return null;
  return (
    <Link to={href} data-testid={testid} className="flex items-center justify-center group">
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className="portal-quicklink-btn object-contain"
        /* v2.6.9 batch CB · Reset to original asset dimensions then scaled
           down to 25 %. Native width ≈ 1164 px → 25 % = 291 px. minWidth
           240 keeps tappability on phones; maxWidth caps the desktop size. */
        style={{ width: "25%", maxWidth: "291px", minWidth: "240px", height: "auto" }}
      />
    </Link>
  );
}

/**
 * v2.6.9 batch BU · CityCard — 1px solid border + drop-shadow + BOOK NOW
 * overlay button per spec. Buttons deep-link to /ride/login with destination +
 * meter_hint prefilled so the rider arrives on the booking form already
 * targeting the right city + meter variant. Submitting that form posts to
 * /api/site/quick-booking which lands in Admin → Book A Ride.
 */
function CityCard({ city }) {
  if (!city) return null;
  const destQuery = encodeURIComponent(city.destination_query || city.name || "");
  const meter = city.meter || "statewide";
  const href = `/ride/login?destination=${destQuery}&meter=${encodeURIComponent(meter)}`;
  return (
    <div
      className="relative rounded-xl overflow-hidden group"
      style={{
        border: "1px solid rgba(250, 204, 21, 0.55)",
        boxShadow: "0 8px 28px rgba(0, 0, 0, 0.45), 0 2px 6px rgba(0, 0, 0, 0.35)",
      }}
      data-testid={`city-card-${city.slug}`}
    >
      {/* v2.6.9 batch BY · The city name + tagline are now BAKED into the
          uploaded asset (WEATHERFORD.png / OKC.png / TULSA.png), so the
          JS-rendered duplicate text overlay was removed. Only the BOOK NOW
          button stays as the actionable overlay. `alt` text is set for
          accessibility but no caption/metadata is rendered visually. */}
      <div className="aspect-[4/3] bg-zinc-900 overflow-hidden">
        {city.image_url ? (
          <img
            src={city.image_url}
            alt={city.name}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
            data-testid={`city-card-${city.slug}-image`}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-zinc-700">
            <MapPin className="h-12 w-12"/>
          </div>
        )}
      </div>
      {/* Subtle bottom-edge gradient so the BOOK NOW button has enough
          contrast over busy photo backgrounds. No text overlay. */}
      <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/85 via-black/40 to-transparent pointer-events-none"/>
      <div className="absolute inset-x-0 bottom-0 p-4">
        <Link to={href} data-testid={`city-card-${city.slug}-book`}>
          <button
            type="button"
            className="w-full bg-amber-400 hover:bg-amber-300 text-zinc-950 font-extrabold uppercase tracking-[0.18em] text-sm py-2.5 rounded-md flex items-center justify-center gap-1 transition-all duration-200 hover:scale-[1.02] shadow-lg shadow-amber-500/40"
          >
            BOOK NOW <ArrowRight className="h-4 w-4"/>
          </button>
        </Link>
      </div>
    </div>
  );
}

// ── v2.7.65+E237 · Premium Services & Add-Ons section ───────────────────
const PREMIUM_ICONS = {
  Sparkles, Plane, Luggage, UserCheck, Gem, Crown, Gift, Wifi, Baby, Dog,
  Snowflake, Star, ShieldCheck, Clock, Car, Briefcase,
};

function PremiumPrice({ it }) {
  const p = (it.price || "").toString().replace(/^\$/, "").trim();
  if (!p) return null;
  const unit = (it.price_unit || "").trim();
  return (
    <div className="mt-3 flex items-baseline gap-1" data-testid="home-premium-price">
      <span className="text-2xl font-black" style={{ color: "#fbbf24" }}>${p}</span>
      {it.price_mode === "per_feature" && unit && (
        <span className="text-xs" style={{ color: "#a1a1aa" }}>{/^per\b/i.test(unit) ? unit : `/ ${unit}`}</span>
      )}
    </div>
  );
}

function PremiumServicesSection({ data }) {
  const items = (data.items || []).filter((i) => i.enabled !== false);
  if (!items.length) return null;
  const useImage = data.bg_mode === "image" && data.bg_image_url;
  const bgStyle = useImage
    ? { backgroundImage: `url(${data.bg_image_url})`, backgroundSize: "cover", backgroundPosition: "center" }
    : { backgroundColor: data.bg_color || "#0b0b0c" };
  return (
    <section className="py-16 border-b border-zinc-900 relative" style={bgStyle} data-testid="home-premium-services">
      {useImage && <div className="absolute inset-0" style={{ backgroundColor: "rgba(9,9,11,0.78)" }} aria-hidden="true"/>}
      <div className="max-w-7xl mx-auto px-5 relative">
        <div className="text-center mb-8">
          <div className="text-[10px] uppercase tracking-[0.4em] font-bold" style={{ color: "#fbbf24" }}>Elevate your ride</div>
          <h2 className="ps-heading text-3xl sm:text-4xl font-black mt-2" style={{ color: "#f5f5f5" }} data-testid="home-premium-heading">
            {data.heading || "Premium Services & Add-Ons"}
          </h2>
          {data.subheading && <p className="text-sm mt-2 max-w-xl mx-auto" style={{ color: "#d4d4d8" }}>{data.subheading}</p>}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="home-premium-grid">
          {items.map((it, i) => {
            const Icon = PREMIUM_ICONS[it.icon] || Sparkles;
            return (
              <div key={it.id || i} style={{ backgroundColor: "rgba(24,24,27,0.94)", borderColor: "#27272a" }} className="rounded-xl border overflow-hidden group" data-testid={`home-premium-item-${i}`}>
                <div style={{ backgroundColor: "#09090b" }} className="h-40 flex items-center justify-center overflow-hidden">
                  {it.image_url
                    ? <img src={it.image_url} alt={it.name || "Premium service"} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" onError={(e) => { e.currentTarget.style.display = "none"; }}/>
                    : <Icon className="h-14 w-14" style={{ color: "rgba(251,191,36,0.85)" }}/>}
                </div>
                <div className="p-5">
                  <div className="text-lg font-black" style={{ color: "#f4f4f5" }}>{it.name || "Premium service"}</div>
                  {it.description && <p className="text-sm mt-1.5 leading-relaxed" style={{ color: "#a1a1aa" }}>{it.description}</p>}
                  <PremiumPrice it={it}/>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ── v2.7.65+E238 · Group Ride Rates section (home, above Premium) ────────
function GroupRideRatesSection({ data }) {
  const items = (data.items || []).filter((i) => i.enabled !== false);
  if (!items.length) return null;
  const useImage = data.bg_mode === "image" && data.bg_image_url;
  const bgStyle = useImage
    ? { backgroundImage: `url(${data.bg_image_url})`, backgroundSize: "cover", backgroundPosition: "center" }
    : { backgroundColor: data.bg_color || "#0b0b0c" };
  return (
    <section className="py-16 border-b border-zinc-900 relative" style={bgStyle} data-testid="home-group-rates">
      {useImage && <div className="absolute inset-0" style={{ backgroundColor: "rgba(9,9,11,0.78)" }} aria-hidden="true"/>}
      <div className="max-w-7xl mx-auto px-5 relative">
        <div className="text-center mb-8">
          <div className="text-[10px] uppercase tracking-[0.4em] font-bold" style={{ color: "#fbbf24" }}>Ride together, save together</div>
          <h2 className="ps-heading text-3xl sm:text-4xl font-black mt-2" style={{ color: "#f5f5f5" }} data-testid="home-group-rates-heading">
            {data.heading || "Group Ride Rates"}
          </h2>
          {data.subheading && <p className="text-sm mt-2 max-w-xl mx-auto" style={{ color: "#d4d4d8" }}>{data.subheading}</p>}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="home-group-grid">
          {items.map((it, i) => (
            <div key={it.id || i} style={{ backgroundColor: "rgba(24,24,27,0.94)", borderColor: "#27272a" }} className="rounded-xl border overflow-hidden group flex flex-col" data-testid={`home-group-item-${i}`}>
              <div style={{ backgroundColor: "#09090b" }} className="h-40 flex items-center justify-center overflow-hidden">
                {it.image_url
                  ? <img src={it.image_url} alt={it.title || "Group ride"} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" onError={(e) => { e.currentTarget.style.display = "none"; }}/>
                  : <Users className="h-14 w-14" style={{ color: "rgba(251,191,36,0.85)" }}/>}
              </div>
              <div className="p-5 flex flex-col flex-1">
                <div className="text-lg font-black" style={{ color: "#f4f4f5" }}>{it.title || "Group ride"}</div>
                {it.description && <p className="text-sm mt-1.5 leading-relaxed flex-1" style={{ color: "#a1a1aa" }}>{it.description}</p>}
                {it.price && <div className="mt-3 text-2xl font-black" style={{ color: "#fbbf24" }}>${(it.price || "").toString().replace(/^\$/, "")}</div>}
                <Link to="/group-rides" data-testid={`home-group-item-${i}-learn`} className="mt-4 inline-flex items-center justify-center gap-1.5 bg-amber-400 hover:bg-amber-300 text-zinc-950 font-black px-4 py-2 rounded-lg text-xs uppercase tracking-wide transition-colors">
                  Learn More <ArrowRight className="h-3.5 w-3.5"/>
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
